<script
    setup
    lang="ts"
    generic="TableRow extends Record<string | number | symbol, any>">
    import {
        type PropType,
        type Slots,
        useSlots,
        computed,
        watch,
        onMounted,
        unref,
    } from 'vue'
    import { useVModel } from '@vueuse/core'
    import { get as dotGet } from 'ts-dot-prop'
    import type { TableColumn, TableColumnRecord } from '../types'
    import PkPagination from './PkPagination.vue'
    import PkTableEmpty from './PkTableEmpty.vue'
    import PkTableError from './PkTableError.vue'

    const slots: Slots = useSlots()

    const props = defineProps({
        columns: {
            type: Array as PropType<TableColumn<TableRow>[]>,
            required: true,
        },
        data: {
            type: Array as PropType<TableRow[] | undefined>,
            default: () => [],
        },
        sort: {
            type: String,
            default: undefined,
        },
        order: {
            type: String as PropType<'asc' | 'desc'>,
            default: 'asc',
            validation: (order: string) => ['asc', 'desc'].includes(order),
        },
        page: {
            type: [String, Number],
            default: undefined,
        },
        total: {
            type: [String, Number],
            default: undefined,
        },
        limit: {
            type: [String, Number],
            default: undefined,
        },
        limitSteps: {
            type: Array as PropType<number[]>,
            default: () => [10, 15, 25, 50, 100],
        },
        nextDisabled: {
            type: Boolean,
            default: false,
        },
        hideHead: {
            type: Boolean,
            default: false,
        },
        hideNavigation: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isError: {
            type: Boolean,
            default: false,
        },
        transitionName: {
            type: String,
            default: 'fade-in',
        },
        classTable: {
            type: String,
            default: undefined,
        },
        // The table sits flush in a frame it does not own, a table accordion
        // (DESIGN.md, principle 10): no frame of its own, and the pagination
        // is a row of that frame. An explicit `classWrapper` or
        // `classPagination` still wins.
        flush: {
            type: Boolean,
            default: false,
        },
        classWrapper: {
            type: String,
            default: undefined,
        },
        classPagination: {
            type: String,
            default: undefined,
        },
        rowClickable: {
            type: Boolean,
            default: false,
        },
        // The row lights up under the pointer without promising a click: on a
        // wide table it is what keeps the eye on one line from the first
        // column to the last. On by default, because a table that answers the
        // pointer next to one that does not is the incoherence this costs the
        // most; pass `:row-hover="false"` where the rows must stay inert.
        // `rowClickable` adds the hand on top of it.
        rowHover: {
            type: Boolean,
            default: true,
        },
        // Classes for one row, decided by the caller: the table owns its
        // `<tr>`, so this is the only way a row can carry a state of its own
        // (`pk-table-sortable__row--current` marks the one you are in).
        rowClass: {
            type: Function as PropType<
                (
                    row: TableRow,
                    index: number,
                ) => string | string[] | Record<string, boolean> | undefined
            >,
            default: undefined,
        },
    })

    const emit = defineEmits([
        'update:sort',
        'update:order',
        'update:page',
        'update:limit',
        'click:row',
    ])

    const hasTotal = computed(() =>
        props.total ? Number(props.total) : undefined,
    )

    const hasLimit = computed({
        get: () => (props.limit !== undefined ? Number(props.limit) : 10),
        set: (newValue) => {
            emit('update:limit', newValue)
        },
    })

    const hasPage = computed({
        get: () => (props.page !== undefined ? Number(props.page) : 1),
        set: (newValue) => {
            emit('update:page', newValue)
        },
    })

    const hasSort = useVModel(props, 'sort', emit)

    const hasOrder = useVModel(props, 'order', emit)

    const hasPageCount = computed(() => {
        if (hasTotal.value !== undefined && hasLimit.value) {
            return Math.ceil(hasTotal.value / hasLimit.value)
        }
        return 0
    })

    const hasColumns = computed(() => {
        return props.columns.reduce<TableColumnRecord<TableRow>[]>(
            (acc, item) => {
                if (typeof item === 'string') {
                    acc.push({
                        name: item,
                        label: item,
                    })
                    return acc
                }
                if (unref(item.hidden)) {
                    return acc
                }
                acc.push(item)
                return acc
            },
            [],
        )
    })

    const hasClassWrapper = computed(
        () =>
            props.classWrapper ??
            (props.flush
                ? 'light-scrollbar'
                : 'light-scrollbar border border-surface-3 rounded-lg'),
    )

    const hasClassPagination = computed(
        () =>
            props.classPagination ??
            (props.flush ? 'px-md py-sm border-t border-surface-3' : undefined),
    )

    const hasNavAndLimitDisabled = computed(() => {
        return props.isLoading || hasPageCount.value === 0
    })

    const hasNextDisabled = computed(() => {
        return (
            hasNavAndLimitDisabled.value ||
            props.nextDisabled ||
            hasPage.value === hasPageCount.value
        )
    })

    const hasPrevDisabled = computed(() => {
        return hasNavAndLimitDisabled.value || hasPage.value === 1
    })

    watch([hasSort, hasOrder, hasLimit], (newValue, oldValue) => {
        if (oldValue && JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            hasPage.value = 1
        }
    })

    watch(hasPageCount, (newValue) => {
        if (newValue && hasPage.value > newValue) {
            hasPage.value = 1
        }
    })

    const onClickSort = (columnName: string) => {
        if (hasSort.value === columnName) {
            hasOrder.value = hasOrder.value === 'asc' ? 'desc' : 'asc'
            return
        }
        hasSort.value = columnName
    }

    // Interactive descendants own their own clicks: the actions dropdown, a
    // link in a cell, a row checkbox. Guarding here rather than asking every
    // caller to stop propagation cell by cell keeps the opt-in to one prop.
    const INTERACTIVE_SELECTOR =
        'a, button, input, select, textarea, label, [contenteditable], [role="button"], [role="link"], [role="checkbox"], [role="menuitem"]'

    const onClickRow = (row: TableRow, index: number, event: MouseEvent) => {
        if (!props.rowClickable) {
            return
        }
        const target = event.target as HTMLElement | null
        if (target?.closest(INTERACTIVE_SELECTOR)) {
            return
        }
        emit('click:row', row, index)
    }

    const onClickNext = () => {
        hasPage.value++
    }

    const onClickPrev = () => {
        hasPage.value--
    }

    // send default values to parent
    onMounted(() => {
        if (!props.page) {
            emit('update:page', hasPage.value)
        }
        if (!props.limit) {
            emit('update:limit', hasLimit.value)
        }
    })
</script>

<template>
    <div class="pk-table-sortable">
        <div :class="hasClassWrapper" class="pk-table-sortable__wrapper">
            <table class="vv-table" :class="classTable">
                <thead v-if="!hideHead">
                    <tr>
                        <th
                            v-for="column in hasColumns"
                            :key="column.name"
                            v-bind="{
                                class: [
                                    column.class,
                                    column.classTh,
                                    {
                                        selected: hasSort === column.name,
                                    },
                                ],
                            }">
                            <div v-if="!column.sortable" class="truncate">
                                <slot :name="`col::header::${column.name}`">
                                    {{ column.label }}
                                </slot>
                            </div>
                            <button
                                v-else
                                type="button"
                                @click="onClickSort(column.name)">
                                <slot :name="`col::header::${column.name}`">
                                    {{ column.label }}
                                </slot>
                                <VvIcon
                                    :class="{
                                        'opacity-30': hasSort !== column.name,
                                    }"
                                    v-bind="
                                        hasSort === column.name
                                            ? {
                                                  name:
                                                      hasOrder === 'asc'
                                                          ? 'ri:arrow-up-line'
                                                          : 'ri:arrow-down-line',
                                              }
                                            : {
                                                  name: 'ri:arrow-up-down-line',
                                              }
                                    " />
                            </button>
                        </th>
                    </tr>
                </thead>
                <TransitionGroup tag="tbody" :name="transitionName">
                    <slot
                        name="tbody"
                        v-bind="{ columns: hasColumns, data, isLoading }">
                        <slot v-if="isLoading" name="loading">
                            <tr v-for="index in hasLimit" :key="index">
                                <td
                                    v-for="{ name, wrapperClass } in hasColumns"
                                    :key="name">
                                    <slot
                                        v-bind="{
                                            index,
                                            wrapperClass,
                                        }"
                                        :name="`skeleton::${name}`">
                                        <div
                                            class="vv-skeleton"
                                            :class="wrapperClass">
                                            <div
                                                class="vv-skeleton__item"></div>
                                        </div>
                                    </slot>
                                </td>
                            </tr>
                        </slot>
                        <template v-else-if="!isError">
                            <template v-if="data?.length">
                                <tr
                                    v-for="(row, index) in data"
                                    :key="index"
                                    :class="[
                                        {
                                            'pk-table-sortable__row--clickable':
                                                rowClickable,
                                            'pk-table-sortable__row--hoverable':
                                                rowHover,
                                        },
                                        rowClass?.(row, index),
                                    ]"
                                    @click="onClickRow(row, index, $event)">
                                    <td
                                        v-for="{
                                            name,
                                            field,
                                            wrapperClass,
                                            render,
                                            ...column
                                        } in hasColumns"
                                        :key="name"
                                        :class="[column.class, column.classTd]">
                                        <template v-if="render">
                                            <component
                                                :is="render(row, index)" />
                                        </template>
                                        <slot
                                            v-else-if="slots[`col::${name}`]"
                                            v-bind="{
                                                row,
                                                field,
                                                wrapperClass,
                                                index,
                                                value: row[name],
                                            }"
                                            :name="`col::${name}`" />
                                        <slot
                                            v-else
                                            v-bind="{
                                                name,
                                                row,
                                                field,
                                                wrapperClass,
                                                index,
                                                value: row[name],
                                            }"
                                            name="col">
                                            <div
                                                class="truncate"
                                                :class="wrapperClass">
                                                {{ dotGet(row, field ?? name) }}
                                            </div>
                                        </slot>
                                    </td>
                                </tr>
                            </template>
                            <tr v-else>
                                <td :colspan="columns.length">
                                    <slot name="empty">
                                        <PkTableEmpty />
                                    </slot>
                                </td>
                            </tr>
                        </template>
                        <tr v-else-if="isError">
                            <td :colspan="columns.length">
                                <slot name="error">
                                    <PkTableError />
                                </slot>
                            </td>
                        </tr>
                    </slot>
                </TransitionGroup>
                <tfoot v-if="slots.tfoot">
                    <slot name="tfoot" />
                </tfoot>
                <caption v-if="slots.caption">
                    <slot name="caption" />
                </caption>
            </table>
        </div>
        <slot
            name="navigation"
            v-bind="{
                hide: hideNavigation,
                prevDisabled: hasPrevDisabled,
                prev: onClickPrev,
                nextDisabled: hasNextDisabled,
                next: onClickNext,
                total: hasPageCount,
                page: hasPage,
            }">
            <PkPagination
                v-if="!hideNavigation"
                :page="hasPage"
                :total="hasTotal ?? 0"
                :limit="hasLimit"
                :limit-steps="limitSteps"
                :disabled="hasNavAndLimitDisabled"
                :class="hasClassPagination"
                @update:page="hasPage = $event"
                @update:limit="hasLimit = $event" />
        </slot>
    </div>
</template>

<style lang="scss">
    .pk-table-sortable {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        min-width: 0;
        min-height: 0;

        &__wrapper {
            width: 100%;
            flex: 1;
            overflow: auto;
        }

        // Only tables that declare `row-hover` or `row-clickable` react to the
        // pointer, so the ones that do not opt in keep their current look. One
        // step of surface, which is a reading aid and not a state: the current
        // row takes two plus a bar, and stays apart.
        &__row--hoverable,
        &__row--clickable {
            transition: background-color var(--duration-150) var(--ease-out);

            &:hover {
                background-color: var(--color-surface-1);
            }
        }

        // The hand belongs to a row that really answers a click, not to one
        // that is merely lit under the pointer.
        &__row--clickable {
            cursor: pointer;
        }

        // A row that is listed but not in use: the models an organization can
        // activate, and has not. It steps back instead of disappearing, and
        // the first cell keeps its full weight, because that is where the
        // control that puts the row back in use lives: a switch at six tenths
        // reads as disabled, and this one is not.
        &__row--muted > td:not(:first-child) {
            opacity: 0.6;
        }

        // "The one you are in" among its peers: the `--current` of the pick
        // lists, drawn on a table row. Written after `--clickable` so it keeps
        // its surface under the hover. The bar goes on the first cell, because
        // a box-shadow on a `<tr>` is not painted by every browser once the
        // table borders collapse.
        &__row--current,
        &__row--current:hover {
            background-color: var(--color-surface-2);

            > td:first-child {
                box-shadow: inset var(--spacing-3) 0 0 var(--color-word-2);
            }
        }

        th {
            text-wrap: nowrap;

            &.selected {
                font-weight: var(--font-bold);
            }

            & > button {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                border: none;
                background: none;
                font-size: inherit;
                font-weight: inherit;
                color: inherit;
                cursor: pointer;
                gap: var(--spacing-8);
            }

            &.text-right {
                & > button {
                    justify-content: flex-end;
                }
            }
        }
    }
</style>
